/**
 * /kitchen — the component base from design spec §7, every variant and every
 * state, in spec order. This is the acceptance surface for Phase B and stays
 * in the tree afterwards as living documentation of the design system.
 *
 * Rule for this file: it may only compose components from src/components/ui.
 * If something here needs a bespoke colour or a one-off size, that is a gap in
 * the component base, not something to patch locally.
 */

import { useState, type ReactNode } from "react";
import {
  Archive,
  Brain,
  Folder,
  Inbox,
  Lightbulb,
  Lock,
  Search,
  Send,
  UserRound,
  Wrench,
} from "lucide-react";

import {
  AttachmentCount,
  DoneIcon,
  DueBadge,
  ErrorCountBadge,
  FilterChip,
  IdeaStatusBadge,
  SupersededBadge,
  TaskStatusBadge,
  ToolKindChip,
  ToolSlugChip,
} from "@/components/ui/Badge";
import { Button, type ButtonVariant } from "@/components/ui/Button";
import { Card, ClickableCard, StaleCard, WarningCard } from "@/components/ui/Card";
import { Cell, CellGroup, SoloCell } from "@/components/ui/Cell";
import { EmptyState, ErrorState } from "@/components/ui/EmptyState";
import { DateField, SearchField, SelectField, TextArea, TextField } from "@/components/ui/Field";
import { ProgressBar, ProgressRing } from "@/components/ui/Progress";
import { GroupLabel, ScreenTitle, SectionTitle } from "@/components/ui/Section";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonCard, SkeletonHeader, SkeletonList, SkeletonStats } from "@/components/ui/Skeleton";
import { Segmented } from "@/components/ui/Segmented";
import { useToast } from "@/components/ui/Toast";
import { ConflictSheet } from "@/components/ConflictSheet";
import { MethodExperienceLine, MethodStatusBadge } from "@/components/MethodBadges";
import { SuggestionCard } from "@/components/SuggestionCard";
import { Status, type StatusTone } from "@/components/ui/Status";
import { VersionDiff } from "@/components/VersionDiff";
import { VersionConflictError } from "@/api/client";
import type { HitOut, MethodVersionOut, TerminalOutcome } from "@/api/types";
import { getColorScheme, setSchemeOverride } from "@/theme/theme";
import type { ColorScheme } from "@/theme/tokens";

/** One labelled specimen. */
function Item({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <GroupLabel>{label}</GroupLabel>
      {children}
    </div>
  );
}


/* ── M5 fixtures (SPEC-2 §6) ──────────────────────────────────────────── */

const DEMO_HIT: HitOut = {
  entity_type: "method",
  entity_id: "00000000-0000-0000-0000-0000000000a1",
  title: "Разбор интервью",
  score: 0.34,
  reason: {
    fts_terms: "Разбор <<интервью>>",
    semantic: true,
    tags: ["контент"],
    project: false,
    status: "proven",
    experience: { applications: 3, success: 2 },
    flags: { erroneous: false, superseded: false, needs_review: false },
  },
  freshness: new Date(Date.now() - 14 * 86_400_000).toISOString(),
};

const DEMO_HIT_FLAGGED: HitOut = {
  ...DEMO_HIT,
  entity_id: "00000000-0000-0000-0000-0000000000a2",
  title: "Приём заявок с сайта",
  reason: {
    ...DEMO_HIT.reason,
    semantic: false,
    tags: [],
    experience: { applications: 1, success: 0 },
    flags: { erroneous: false, superseded: false, needs_review: true },
  },
  freshness: new Date(Date.now() - 400 * 86_400_000).toISOString(),
};

const DEMO_V1: MethodVersionOut = {
  id: "v1",
  method_id: "m1",
  number: 1,
  body_md: ["1. Расшифровать запись", "2. Вытащить тезисы"].join("\n"),
  preconditions_md: "когда есть аудиозапись",
  checks_md: "цитаты сверены с записью",
  pitfalls_md: null,
  change_note: null,
  based_on_version_id: null,
  author_id: "u1",
  created_at: new Date(Date.now() - 60 * 86_400_000).toISOString(),
};

const DEMO_V2: MethodVersionOut = {
  ...DEMO_V1,
  id: "v2",
  number: 2,
  body_md: ["1. Расшифровать запись", "2. Разбить на блоки по вопросам", "3. Вытащить тезисы"].join(
    "\n",
  ),
  pitfalls_md: "не годится для групповых интервью",
  change_note: "Добавили разбиение на блоки перед тезисами",
  based_on_version_id: "v1",
  created_at: new Date(Date.now() - 3 * 86_400_000).toISOString(),
};

const DEMO_CONFLICT = new VersionConflictError(3, 5, { title: "Название партнёра" });

function Group({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-t border-sep pt-4">
      <SectionTitle>
        {n}. {title}
      </SectionTitle>
      {children}
    </section>
  );
}

const BUTTON_VARIANTS: ButtonVariant[] = [
  "primary",
  "strong",
  "secondary",
  "ghost",
  "destructive",
  "destructive-outline",
];

const STATUS_TONES: StatusTone[] = ["now", "done", "review", "needs", "blocked", "waiting"];

const IN_A_WEEK = new Date(Date.now() + 7 * 86_400_000);
const YESTERDAY = new Date(Date.now() - 86_400_000);

export function KitchenView() {
  const toast = useToast();
  const [outcome, setOutcome] = useState<TerminalOutcome>("success");
  const [conflictOpen, setConflictOpen] = useState(false);
  const [scheme, setScheme] = useState<ColorScheme>(getColorScheme());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [keyboardSheetOpen, setKeyboardSheetOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [project, setProject] = useState("");
  const [date, setDate] = useState("");
  const [query, setQuery] = useState("");

  const pinScheme = (next: ColorScheme) => {
    setSchemeOverride(next);
    setScheme(next);
  };

  return (
    <div className="flex flex-col gap-5">
      <ScreenTitle eyebrow="design spec §7">Компонентная база</ScreenTitle>

      <Card>
        <p className="mb-2 text-meta text-hint">
          Тема пришпилена только на этом экране — остальное приложение следует за клиентом.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="compact"
            variant={scheme === "light" ? "strong" : "secondary"}
            onClick={() => pinScheme("light")}
          >
            Светлая
          </Button>
          <Button
            size="compact"
            variant={scheme === "dark" ? "strong" : "secondary"}
            onClick={() => pinScheme("dark")}
          >
            Тёмная
          </Button>
          <Button size="compact" variant="ghost" onClick={() => setSchemeOverride(null)}>
            Как в клиенте
          </Button>
        </div>
      </Card>

      {/* ── 1. Кнопка ─────────────────────────────────────────────────── */}
      <Group n={1} title="Кнопка">
        {BUTTON_VARIANTS.map((variant) => (
          <Item key={variant} label={variant}>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button variant={variant}>Обычная</Button>
                <Button variant={variant} size="compact">
                  Компактная
                </Button>
                <Button variant={variant} size="icon" aria-label="Отправить">
                  <Send className="h-[18px] w-[18px]" aria-hidden="true" />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant={variant} disabled>
                  Отключена
                </Button>
                <Button variant={variant} loading loadingText="Сохраняем…">
                  Сохранить
                </Button>
              </div>
            </div>
          </Item>
        ))}
        <Item label="растянутая на строку">
          <Button block>Одобрить</Button>
        </Item>
        <Item label="пара из шторки идеи (§8) — 120px + растущая">
          <div className="flex gap-2">
            <Button variant="destructive-outline" className="w-[120px] shrink-0">
              Отклонить
            </Button>
            <Button className="flex-1">Одобрить</Button>
          </div>
        </Item>
      </Group>

      {/* ── 2. Ячейка ─────────────────────────────────────────────────── */}
      <Group n={2} title="Ячейка (Cell)">
        <Item label="одиночная — свой радиус 10">
          <SoloCell title="Проект" value="Сайт студии" chevron />
        </Item>
        <Item label="группа без иконок — разделитель с отступом 12">
          <CellGroup>
            <Cell title="Проект" value="Сайт студии" chevron />
            <Cell title="Исполнитель" value="Костя" chevron />
            <Cell title="Срок" value="22 июля" />
          </CellGroup>
        </Item>
        <Item label="группа с иконками — разделитель с отступом 42">
          <CellGroup inset="icon">
            <Cell icon={<Wrench className="h-5 w-5" />} title="Инструменты" value="7" chevron />
            <Cell icon={<Archive className="h-5 w-5" />} title="Архив проектов" chevron />
            <Cell
              icon={<UserRound className="h-5 w-5" />}
              title="Роль"
              subtitle="Определяет доступ к ревью идей"
              value="admin"
            />
          </CellGroup>
        </Item>
        <Item label="состояния: обычная, отключённая (.4), выполненная, деструктивная">
          <CellGroup inset="icon">
            <Cell
              icon={<Folder className="h-5 w-5" />}
              title="Обычная"
              subtitle="С подписью на второй строке"
              chevron
            />
            <Cell icon={<Lock className="h-5 w-5" />} title="Отключена" disabled chevron />
            <Cell icon={<DoneIcon />} title="Настроить домен и SSL" done />
            <Cell icon={<Lock className="h-5 w-5" />} title="Выйти" destructive />
          </CellGroup>
        </Item>
      </Group>

      {/* ── 3. Карточка ───────────────────────────────────────────────── */}
      <Group n={3} title="Карточка">
        <Item label="обычная — --sbg, радиус 10, паддинг 12">
          <Card>
            <p className="text-cell text-text">
              Разделение слоёв только за счёт --bg / --sbg и границ --sep. Ни теней, ни градиентов.
            </p>
          </Card>
        </Item>
        <Item label="кликабельная — chevron справа, тап по всей карточке">
          <ClickableCard onClick={() => toast.success("Тап по всей карточке")}>
            <p className="text-cell-title font-medium text-text">Собрать КП для клиента</p>
            <p className="mt-1 text-meta text-hint">Сайт студии · Костя</p>
          </ClickableCard>
        </Item>
        <Item label="с предупреждением — рамка --dstr, шапка --dstr-soft, тело в полном контрасте">
          <WarningCard
            reason={
              <>
                <strong className="font-medium">Помечено ошибочным.</strong> Ссылку на репозиторий
                перепутали с соседним проектом. Запись оставлена как есть: её нельзя удалить или
                отредактировать
              </>
            }
          >
            <p className="text-cell-title font-medium text-text">Подключил аналитику</p>
            <p className="mt-1 text-body text-hint">
              Поставил счётчик через GTM, потому что доступа к коду шаблона не было.
            </p>
          </WarningCard>
        </Item>
        <Item label="устаревшая (FEAT-025) — рамка --sep, шапка --sbg, clock в --hint: подход был верным, изменились обстоятельства">
          <StaleCard
            reason={
              <>
                <strong className="font-medium">Устарело.</strong> Библиотеку сняли с поддержки в
                2026-м. Запись оставлена как есть
              </>
            }
          >
            <p className="text-cell-title font-medium text-text">Собрал график на chart-lib</p>
            <p className="mt-1 text-body text-hint">
              Взяли chart-lib — на тот момент единственная с нужным типом диаграммы.
            </p>
          </StaleCard>
        </Item>
      </Group>

      {/* ── 4. Бейджи и чипы ──────────────────────────────────────────── */}
      <Group n={4} title="Бейджи и чипы">
        <Item label="Spec 020 D3 — статусы: только этот набор, точка и слово">
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_TONES.map((tone) => <Status key={tone} tone={tone} />)}
          </div>
        </Item>
        <Item label="статусы задач + сроки">
          <div className="flex flex-wrap items-center gap-1.5">
            <TaskStatusBadge category="in_progress" />
            <TaskStatusBadge category="backlog" />
            <TaskStatusBadge category="done" />
            <TaskStatusBadge category="cancelled" />
            <DueBadge due={YESTERDAY} />
            <DueBadge due={new Date()} />
            <DueBadge due={IN_A_WEEK} />
          </div>
        </Item>
        <Item label="статусы идей">
          <div className="flex flex-wrap items-center gap-1.5">
            <IdeaStatusBadge status="new" />
            <IdeaStatusBadge status="reviewing" />
            <IdeaStatusBadge status="approved" />
            <IdeaStatusBadge status="rejected" />
            <IdeaStatusBadge status="converted" />
          </div>
        </Item>
        <Item label="тип инструмента — agent акцентный как основной исполнитель">
          <div className="flex flex-wrap items-center gap-1.5">
            {(["agent", "skill", "script", "template", "service", "other"] as const).map((k) => (
              <ToolKindChip key={k} kind={k} />
            ))}
          </div>
        </Item>
        <Item label="флаги прецедента — ошибочный (--dstr) и устаревший (--sep/--hint): разные утверждения, не два уровня одного">
          <div className="flex flex-wrap items-center gap-1.5">
            <ErrorCountBadge count={1} />
            <ErrorCountBadge count={3} />
            <SupersededBadge />
          </div>
        </Item>
        <Item label="чипы инструментов в записи журнала">
          <div className="flex flex-wrap items-center gap-1.5">
            <ToolSlugChip slug="prosto-task" />
            <ToolSlugChip slug="whisper" />
            <ToolSlugChip slug="ffmpeg" />
          </div>
        </Item>
        <Item label="счётчик вложений (без плашки) и предупреждение об ошибках">
          <div className="flex flex-wrap items-center gap-3">
            <AttachmentCount count={3} />
            <ErrorCountBadge count={1} />
            <ErrorCountBadge count={2} />
            <ErrorCountBadge count={5} />
          </div>
        </Item>
        <Item label="фильтр-чипы — активный залит --btn">
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: "all", label: "Все", n: 12 },
              { id: "new", label: "Новые", n: 4 },
              { id: "approved", label: "Одобренные", n: 5 },
              { id: "rejected", label: "Отклонённые", n: 3 },
            ].map((f) => (
              <FilterChip
                key={f.id}
                active={filter === f.id}
                count={f.n}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </FilterChip>
            ))}
          </div>
        </Item>
      </Group>

      {/* ── 5. «Выполнено» без зелёного ───────────────────────────────── */}
      <Group n={5} title="«Выполнено» без зелёного">
        <Item label="иконка + line-through + opacity .6 — три сигнала одновременно">
          <Card className="flex items-center gap-2.5">
            <DoneIcon />
            <span className="done-strike done-dim flex-1 text-cell-title text-hint">
              Настроить домен и SSL
            </span>
            <TaskStatusBadge category="done" />
          </Card>
        </Item>
        <Item label="для сравнения — акцент достаётся только активному">
          <Card className="flex items-center gap-2.5">
            <span className="flex-1 text-cell-title font-medium text-text">
              Собрать КП для клиента
            </span>
            <TaskStatusBadge category="in_progress" />
          </Card>
        </Item>
      </Group>

      {/* ── 6. Поля ввода ─────────────────────────────────────────────── */}
      <Group n={6} title="Поля ввода">
        <Item label="текстовое — высота ≥44, фокус даёт рамку --accent">
          <TextField
            label="Заголовок задачи"
            placeholder="Что нужно сделать"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Item>
        <Item label="многострочное — min-height 88">
          <TextArea
            label="Причина отклонения"
            placeholder="Почему идея не идёт в работу"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            hint="Причина уходит в память команды."
          />
        </Item>
        <Item label="селект — ячейка со значением и chevron-down">
          <SelectField
            label="Привязать к проекту"
            value={project}
            onChange={setProject}
            placeholder="Без проекта"
            options={[
              { value: "1", label: "Сайт студии" },
              { value: "2", label: "Внутренние инструменты" },
              { value: "3", label: "Клиентские КП" },
            ]}
          />
        </Item>
        <Item label="дата — иконка calendar-days, значение цветом --accent">
          <DateField label="Срок" value={date} onChange={setDate} />
        </Item>
        <Item label="ошибка — рамка --dstr + подпись 12px под полем">
          <TextField
            label="Заголовок задачи"
            value=""
            onChange={() => undefined}
            error="Заголовок не может быть пустым"
            placeholder="Что нужно сделать"
          />
        </Item>
        <Item label="поиск — 36px, радиус 8">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Поиск инструментов"
            icon={<Search className="h-4 w-4 shrink-0 text-hint" aria-hidden="true" />}
          />
        </Item>
      </Group>

      {/* ── 7. Шторка ─────────────────────────────────────────────────── */}
      <Group n={7} title="Нижняя шторка (Sheet)">
        <Item label="обычная — грабер 36×4, скрим .45, максимум 85% высоты">
          <Button variant="secondary" onClick={() => setSheetOpen(true)}>
            Открыть шторку
          </Button>
        </Item>
        <Item label="с клавиатурой — подъём по visualViewport, кнопка disabled пока поле пусто">
          <Button variant="secondary" onClick={() => setKeyboardSheetOpen(true)}>
            Открыть шторку отклонения
          </Button>
        </Item>
      </Group>

      {/* ── 8. Тост ───────────────────────────────────────────────────── */}
      <Group n={8} title="Тост">
        <Item label="успех — над таб-баром, фон --sbg, иконка check цветом --accent">
          <div className="flex flex-wrap gap-2">
            <Button
              size="compact"
              variant="secondary"
              onClick={() => toast.success("Идея одобрена — создана задача")}
            >
              Успех
            </Button>
            <Button
              size="compact"
              variant="secondary"
              onClick={() =>
                toast.success("Идея отклонена", {
                  action: { label: "Отменить", onClick: () => toast.success("Отменено") },
                })
              }
            >
              Успех с действием
            </Button>
          </div>
        </Item>
        <Item label="ошибка — под шапкой Telegram, заливка --dstr, текст #FFFFFF">
          <div className="flex flex-wrap gap-2">
            <Button
              size="compact"
              variant="secondary"
              onClick={() => toast.error("Не удалось загрузить идеи")}
            >
              Ошибка
            </Button>
            <Button
              size="compact"
              variant="secondary"
              onClick={() => toast.error("Данные могут быть устаревшими", { dismissible: true })}
            >
              Ошибка с крестиком
            </Button>
          </div>
        </Item>
      </Group>

      {/* ── 9. Прогресс ───────────────────────────────────────────────── */}
      <Group n={9} title="Прогресс">
        <Item label="полоса 4px — пусто / частично / завершено (заполнение --hint)">
          <div className="flex flex-col gap-3">
            <ProgressBar done={0} total={8} />
            <ProgressBar done={3} total={8} />
            <ProgressBar done={8} total={8} />
          </div>
        </Item>
        <Item label="кольцо — conic-gradient, в центре доля или процент">
          <div className="flex items-center gap-4">
            <ProgressRing done={3} total={8} />
            <ProgressRing done={3} total={8} label="percent" size={56} />
            <ProgressRing done={8} total={8} size={56} />
          </div>
        </Item>
      </Group>

      {/* ── 10. Скелетоны ─────────────────────────────────────────────── */}
      <Group n={10} title="Скелетоны">
        <Item label="форма контента, пульсация 1.5с со сдвигом 0.1с, последний элемент .5">
          <div className="flex flex-col gap-4">
            <SkeletonHeader />
            <SkeletonStats />
            <SkeletonList />
            <SkeletonCard />
          </div>
        </Item>
      </Group>

      {/* ── 11. Пустые состояния и ошибка ─────────────────────────────── */}
      <Group n={11} title="Пустые состояния и ошибка">
        <Item label="пусто — с конкретным действием">
          <EmptyState
            icon={<Lightbulb className="h-8 w-8" />}
            title="Идей пока нет"
            description="Скинь мысль голосом в бота — она появится здесь и дождётся ревью."
            action={
              <Button size="compact" onClick={() => toast.success("Заглушка")}>
                Как отправить идею
              </Button>
            }
          />
        </Item>
        <Item label="пусто — без действия">
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="Ничего не назначено"
            description="Задачи появятся, когда партнёр одобрит идею или заведёт их в проекте."
          />
        </Item>
        <Item label="ошибка загрузки — cloud-off цветом --dstr, контурная «Повторить»">
          <ErrorState
            title="Не удалось загрузить идеи"
            description="Сервер не ответил. Проверь соединение и попробуй ещё раз."
            onRetry={() => toast.error("Всё ещё не отвечает")}
          />
        </Item>
        <Item label="свёрнутая секция памяти (§8) — вся строка ≥44">
          <Card>
            <div className="flex items-center gap-2">
              <Brain className="h-[18px] w-[18px] text-hint" aria-hidden="true" />
              <span className="text-section font-bold text-text">Похожее из памяти</span>
              <span className="text-section text-hint">3</span>
            </div>
          </Card>
        </Item>
      </Group>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Настроить домен и SSL"
        footer={
          <div className="flex gap-2">
            <Button
              variant="destructive-outline"
              className="w-[120px] shrink-0"
              onClick={() => setSheetOpen(false)}
            >
              Отклонить
            </Button>
            <Button className="flex-1" onClick={() => setSheetOpen(false)}>
              Одобрить
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-cell text-text">
            Шторка растёт по контенту и упирается в 85% высоты экрана. Закрытие — тап по скриму,
            Escape или системный BackButton; своей кнопки «назад» и крестика у неё нет.
          </p>
          <CellGroup>
            <Cell title="Проект" value="Сайт студии" chevron />
            <Cell title="Автор" value="Костя" />
          </CellGroup>
        </div>
      </Sheet>

      {/* ── M5 additions (SPEC-2 §6) ─────────────────────────────────── */}

      <Group n={12} title="Сегмент-контрол">
        <p className="text-meta text-hint">
          Все варианты видны сразу. Выпадающий список здесь был бы хуже: «частично» и
          «не сработало» говорят о методе, «бросили» — о том, что работа встала по
          другой причине, и различить их можно только видя все три рядом.
        </p>
        <Segmented
          label="Исход применения"
          options={[
            { value: "success", label: "сработало", hint: "Метод дал то, ради чего его брали." },
            { value: "partial", label: "частично", hint: "Помог, но не полностью." },
            { value: "failed", label: "не сработало", hint: "Результата не дал." },
            { value: "abandoned", label: "бросили", hint: "Встали по причинам не про метод." },
          ]}
          value={outcome}
          onChange={setOutcome}
        />
      </Group>

      <Group n={13} title="Статусы метода">
        <p className="text-meta text-hint">
          Акцент достаётся только «проверен» — это единственное утверждение в библиотеке,
          подкреплённое доказательством, которое проверил сервер.
        </p>
        <div className="flex flex-wrap gap-2">
          <MethodStatusBadge status="draft" />
          <MethodStatusBadge status="proven" />
          <MethodStatusBadge status="needs_review" />
          <MethodStatusBadge status="archived" />
        </div>
        <MethodExperienceLine versionNumber={3} applications={4} success={3} />
        <MethodExperienceLine versionNumber={1} applications={0} success={0} />
      </Group>

      <Group n={14} title="Карточка-подсказка">
        <p className="text-meta text-hint">
          Причина обязательна. Подсказка без объяснения — это гадание, которое человек не
          может проверить, и после второго промаха он перестаёт смотреть на блок вовсе.
        </p>
        <SuggestionCard hit={DEMO_HIT} onApply={() => undefined} onDismiss={() => undefined} />
        <SuggestionCard hit={DEMO_HIT_FLAGGED} onDismiss={() => undefined} />
      </Group>

      <Group n={15} title="Diff версий">
        <p className="text-meta text-hint">
          Посекционно, а не пословно: вопрос «что сдвинулось» отвечается одним взглядом, а
          пословная подсветка заставляет читать запятые наравне с выброшенным шагом.
        </p>
        <VersionDiff before={DEMO_V1} after={DEMO_V2} />
      </Group>

      <Group n={16} title="Конфликт версий (409)">
        <p className="text-meta text-hint">
          Кнопки «повторить» нет намеренно: повтор с той же версией снова упадёт, а без
          версии — молча затрёт чужую правку. Оба слепых хода недоступны из интерфейса.
        </p>
        <Button variant="secondary" onClick={() => setConflictOpen(true)}>
          Показать шторку конфликта
        </Button>
        {conflictOpen && (
          <ConflictSheet
            conflict={DEMO_CONFLICT}
            attempted={{ title: "Моё название" }}
            onReload={() => setConflictOpen(false)}
            onCancel={() => setConflictOpen(false)}
          />
        )}
      </Group>

      <Sheet
        open={keyboardSheetOpen}
        onClose={() => setKeyboardSheetOpen(false)}
        title="Отклонить идею"
        footer={
          <Button
            variant="destructive"
            block
            disabled={reason.trim().length === 0}
            onClick={() => {
              setKeyboardSheetOpen(false);
              toast.success("Идея отклонена", {
                action: { label: "Отменить", onClick: () => undefined },
              });
            }}
          >
            Отклонить
          </Button>
        }
      >
        <TextArea
          label="Причина"
          placeholder="Почему идея не идёт в работу"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint="Кнопка станет активной, как только появится текст. Причина уходит в память команды."
        />
      </Sheet>
    </div>
  );
}
